import { EMPTY_PLACEHOLDER } from '@patsnap/synapse_common_config'
import { defineComponent, PropType } from '@vue/composition-api'
import { isUndefined } from 'lodash'
import { useLocale } from '../../../composition/useLocale'
import { IDrugDetail, IGTableColumnsProps } from '../../../types'
import { GDescription } from '../../ui/GDescription/GDescription'
import { GTable } from '../../ui/GTable/GTable'
import {
  BDrugAdcAntibodyField,
  BDrugAdcAntibodySequenceTrigger,
  BDrugAdcAntibodyTypeField,
  BDrugAdcConjugationSiteField,
  BDrugAdcDarField,
  BDrugAdcLinkerField,
  BDrugAdcLinkerTypeField,
  BDrugAdcPayloadField,
  BDrugAdcSiteSpecialField,
  BDrugAdcSourceField,
  BDrugAdcStructureTrigger,
  BDrugAdcTargetField,
  BDrugAdcTargetingMoietyTypeField,
} from './BDrugAdcField'
import $classes from './BDrugAdcFieldTable.module.scss'
import cn from './locales/cn.json'
import en from './locales/en.json'

export const BDrugAdcFieldTable = defineComponent({
  name: 'BDrugAdcFieldTable',
  i18n: {
    messages: {
      cn,
      en,
    },
  },
  props: {
    data: {
      type: Object as PropType<IDrugDetail>,
      required: true,
    },
    dialogEnable: {
      type: Boolean,
      default: true,
    },
  },
  setup(props, ctx) {
    const { localeUpcase, isCN } = useLocale()

    function onSequenceSimilar(sequence_hash_id: string) {
      ctx.emit('sequenceSimilar', sequence_hash_id)
    }
    function onStructureSimilar(inchiKey: Exclude<string, ''>) {
      ctx.emit('structureSimilar', inchiKey)
    }

    return {
      isCN,
      localeUpcase,
      onSequenceSimilar,
      onStructureSimilar,
    }
  },
  methods: {
    renderTargetMoiety(data: IDrugDetail) {
      return (
        <GDescription
          class={$classes.description}
          data={data}
          width={this.isCN ? 36 : 46}
          gapSize="mini"
          items={[
            { label: this.$tc('name'), field: 'name' },
            { label: this.$tc('type'), field: 'type' },
            { label: this.$tc('target'), field: 'target' },
          ]}
          scopedSlots={{
            name: () => <BDrugAdcAntibodyField data={data}></BDrugAdcAntibodyField>,
            type: () => {
              if (!data.target_moiety_type_view?.length && !data.antibody_type_view) return <span>{EMPTY_PLACEHOLDER}</span>
              return (
                <div>
                  {data.target_moiety_type_view?.length ? <BDrugAdcTargetingMoietyTypeField data={data} limitCount={1} appendToBody /> : null}
                  {data.target_moiety_type_view?.length && data.antibody_type_view ? <span>, </span> : null}
                  {data.antibody_type_view ? <BDrugAdcAntibodyTypeField data={data} class="text-gray-450"></BDrugAdcAntibodyTypeField> : null}
                </div>
              )
            },
            target: () => <BDrugAdcTargetField target={data.moiety_target_id_view || []} limitCount={1} appendToBody />,
          }}
        ></GDescription>
      )
    },
    renderLinker(data: IDrugDetail) {
      return (
        <GDescription
          class={$classes.description}
          data={data}
          width={this.isCN ? 36 : 46}
          gapSize="mini"
          items={[
            { label: this.$tc('name'), field: 'name' },
            { label: this.$tc('type'), field: 'type' },
          ]}
          scopedSlots={{
            name: () => <BDrugAdcLinkerField data={data} class="leading-6 text-gray-450"></BDrugAdcLinkerField>,
            type: () => <BDrugAdcLinkerTypeField data={data} class="leading-6 text-gray-450"></BDrugAdcLinkerTypeField>,
          }}
        ></GDescription>
      )
    },
    renderPayload(data: IDrugDetail) {
      return (
        <GDescription
          class={$classes.description}
          data={data}
          width={this.isCN ? 36 : 46}
          gapSize="mini"
          items={[
            { label: this.$tc('name'), field: 'name' },
            { label: this.$tc('target'), field: 'target' },
          ]}
          scopedSlots={{
            name: () => <BDrugAdcPayloadField data={data} class="leading-6 text-gray-450"></BDrugAdcPayloadField>,
            target: () => <BDrugAdcTargetField target={data.payload_target_id_view || []} limitCount={1} appendToBody />,
          }}
        ></GDescription>
      )
    },
  },
  render() {
    const tableData = [this.data]

    const columns: (IGTableColumnsProps<IDrugDetail> & { show?: () => boolean | boolean })[] = [
      {
        label: this.$tc('synapse_i18n.drug.adc.antibody'),
        minWidth: '240px',
        renderCell: (h, item) => {
          // 当XDC药物同时存在自身结构和 偶联类型结构时，保留自身结构，去除偶联类型下的「查看序列」按钮；若XDC无自身序列，但是偶联药物有序列，则保留「查看序列」按钮。
          // From: https://confluence.zhihuiya.com/display/DLS/SNP+202406+Online+UAT 第33点
          const sequence = !item.row.sequence?.length ? item.row.antibody_drug_id_view?.sequence : []
          return (
            <div style="line-height: 0;" class="flex flex-col gap-y-2">
              {this.renderTargetMoiety(item.row)}
              {sequence?.length ? (
                <BDrugAdcAntibodySequenceTrigger
                  sequence={sequence}
                  triggerText={this.localeUpcase === 'CN' ? '查看序列' : 'View Sequence'}
                  onClickSimilar={this.onSequenceSimilar}
                ></BDrugAdcAntibodySequenceTrigger>
              ) : null}
            </div>
          )
        },
      },
      {
        label: this.$tc('synapse_i18n.drug.adc.linker'),
        minWidth: '240px',
        renderCell: (h, item) => {
          return (
            <div style="line-height: 0;" class="flex items-start gap-x-1">
              {this.renderLinker(item.row)}
              {item.row.structure?.filter((i) => i.structure_type === 'LINKER')?.length && this.dialogEnable ? (
                <BDrugAdcStructureTrigger
                  field="linker_id_view"
                  data={item.row}
                  onClickSimilar={this.onStructureSimilar}
                  onCasStructureItemClicked={(index: number) => this.$emit('linkerStructureItemClicked', index)}
                  triggerMode="icon"
                ></BDrugAdcStructureTrigger>
              ) : null}
            </div>
          )
        },
      },
      {
        label: this.$tc('synapse_i18n.drug.adc.payload'),
        minWidth: '240px',
        renderCell: (h, item) => {
          return (
            <div style="line-height: 0;" class="flex items-start gap-x-1">
              {this.renderPayload(item.row)}
              {item.row.structure?.filter((i) => i.structure_type === 'PAYLOAD')?.length && this.dialogEnable ? (
                <BDrugAdcStructureTrigger
                  field="payload_id_view"
                  data={item.row}
                  onClickSimilar={this.onStructureSimilar}
                  onCasStructureItemClicked={(index: number) => this.$emit('casStructureItemClicked', index)}
                  triggerMode="icon"
                ></BDrugAdcStructureTrigger>
              ) : null}
            </div>
          )
        },
      },
      {
        label: this.$tc('synapse_i18n.drug.adc.dar'),
        minWidth: '80px',
        renderCell: (h, item) => {
          if (isUndefined(item.row.dar)) return <span>{EMPTY_PLACEHOLDER}</span>

          return (
            <div style="line-height: 0;">
              <span class="text-sm leading-6 text-text-t1">
                <BDrugAdcDarField data={item.row} />
              </span>
            </div>
          )
        },
      },
      {
        label: this.$tc('synapse_i18n.drug.adc.conjugationSite'),
        minWidth: '160px',
        renderCell: (h, item) => {
          if (!item.row.site_id_view) return <span>{EMPTY_PLACEHOLDER}</span>
          return (
            <div style="line-height: 0;">
              <span class="text-sm leading-6 text-text-t1">
                <BDrugAdcConjugationSiteField data={item.row}></BDrugAdcConjugationSiteField>
              </span>
            </div>
          )
        },
      },
      {
        label: this.$tc('synapse_i18n.drug.adc.siteSpecial'),
        minWidth: '80px',
        renderCell: (h, item) => {
          if (isUndefined(item.row.specific_site)) return <span>{EMPTY_PLACEHOLDER}</span>
          return (
            <div style="line-height: 0;">
              <span class="text-sm leading-6 text-text-t1">
                <BDrugAdcSiteSpecialField data={item.row}></BDrugAdcSiteSpecialField>
              </span>
            </div>
          )
        },
      },
      {
        label: this.$tc('synapse_i18n.drug.adc.source'),
        minWidth: '100px',
        renderCell: (h, item) => {
          return (
            <div style="line-height: 0;">
              <BDrugAdcSourceField class="text-sm leading-6" data={item.row} triggerMode="icon"></BDrugAdcSourceField>
            </div>
          )
        },
      },
    ]
    const filteredColumns = columns.filter((column) => {
      if ('show' in column) {
        return typeof column.show === 'function' ? column.show() : column.show
      }
      return true
    })

    return <GTable data={tableData} columns={filteredColumns} border></GTable>
  },
})
